// @ts-nocheck
import Typography from "@store/MobiusOverrides/Typography/Typography";
import { lazyImageStyles } from "@store/Themes/common/mixins/lazyload";
import getColor from "@insite/mobius/utilities/getColor";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import Clickable from "@insite/mobius/Clickable";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyImage, { LazyImagePresentationProps } from "@store/MobiusOverrides/LazyImage/LazyImage";
import {
    AvatarDisplaySize,
    JustifiedAlignment,
    TitlePosition,
} from "@store/Library/Widgets/Category/CategoryAvatarList";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { getSettingsCollection } from "@toolkit/Framework/Store/Context/ContextSelectors";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import styled, { css } from "styled-components";

interface CategoryAvatarStyles {
    container?: InjectableCss;
    avatar?: InjectableCss;
    avatarImage?: InjectableCss;
    avatarText?: TypographyPresentationProps;
    svg?: InjectableCss;
    circle?: InjectableCss;
}

interface CategoryAvatarProps {
    extendedStyles?: CategoryAvatarStyles;
    overrideStyles?: CategoryAvatarStyles;
    avatarColor?: string;
    size?: AvatarDisplaySize;
    category: Category;
    titlePosition: TitlePosition;
    imageSize?: string;
    containerWidth?: string;
    containerHeight?: string;
    alignment?: JustifiedAlignment;
    transitionColor?: string;
}

const StyledCircle = styled.circle<InjectableCss>`
    ${injectCss}
`;

const StyledSvg = styled.svg<InjectableCss>`
    position: absolute;
    overflow: visible;
    ${props => props.css}
`;
const categoryAvatarStyles: CategoryAvatarStyles = {
    svg: {
        css: css`
            position: "absolute";
            overflow: "visible";
        `,
    },
    circle: {
        css: css`
            stroke-linecap: round;
            fill: transparent;
            position: relative;
            z-index: 10;
            fill: transparent;
            transform: rotate(-90deg);
            transform-origin: center;
        `,
    },
    avatarImage: {
        css: css`
            ${lazyImageStyles()}
            transition: all 0.5s ease-in-out;
            position: relative;
            border-radius: 50%;
            width: 100%;
            height: 100%;

            img {
                position: absolute;
                width: 100%;
                height: 100%;
                border-radius: 50%;
                object-fit: cover;
                transition: all 0.5s ease-in-out;
            }
        `,
    },
    avatarText: {
        variant: "h4",
        css: css`
            text-align: center;
        `,
    },
    avatar: {
        css: css`
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-evenly;
            padding: 0%;
            cursor: pointer;
        `,
    },

    container: {
        css: css`
            h4 {
                transition: color 0.5s ease-in-out;
            }
            &:hover {
                h4 {
                    color: ${getColor("secondary")};
                }
            }
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-evenly;
            cursor: pointer;
        `,
    },
};
const CategoryAvatar: React.FC<CategoryAvatarProps> = ({
    extendedStyles,
    overrideStyles,
    category,
    titlePosition,
    imageSize,
    containerWidth,
    containerHeight,
    alignment,
    transitionColor,
}) => {
    const styles = useExpandProps(categoryAvatarStyles, extendedStyles, overrideStyles);
    const imageRef = React.useRef<HTMLDivElement>(null);
    const [avatarSize, setAvatarSize] = React.useState<number>(0);
    const defaultImagePath = useSelector(
        (state: ApplicationState) =>
            getSettingsCollection(state).extensionCatalogSettings?.notFoundCategorySmallImagePath,
    );
    const [isHovering, setIsHovering] = React.useState<boolean>(false);

    useEffect(() => {
        if (imageRef.current) {
            const width = imageRef.current.offsetWidth;
            setAvatarSize(width);
        }
    }, [imageRef.current?.offsetWidth]);
    const circleLength = 2 * avatarSize * Math.PI;

    const avatarStyles: InjectableCss = {
        css: css`
            ${styles.avatar?.css}
            width: ${imageSize};
            height: ${imageSize};
            transform: scale(${isHovering ? 1.02 : 1});
            transition: transform 0.5s ease-in-out;
        `,
    };
    const containerStyles: InjectableCss = {
        css: css`
            ${styles.container?.css}
            width: ${containerWidth};
            height: ${containerHeight};
            ${alignment ? `justify-content: ${alignment};` : ""}
        `,
    };

    const circleStyles: InjectableCss = {
        css: css`
            ${styles.circle?.css}
            stroke-dasharray: ${circleLength};
            stroke-dashoffset: ${isHovering ? 0 : circleLength};
            stroke-width: ${isHovering ? "10px" : "5px"};
            transition: stroke-width ${isHovering ? "0.5s" : "0s"} 0.2s ease-in-out, stroke-dashoffset 0.5s ease-in;
            stroke: ${transitionColor};
        `,
    };
    return (
        <Clickable {...containerStyles} mergeCss href={category.path}>
            {titlePosition === "above" && (
                <Typography {...styles.avatarText} mergeCss>
                    {category.shortDescription}
                </Typography>
            )}
            <StyledWrapper
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                ref={imageRef}
                {...avatarStyles}
            >
                {circleLength !== 0 && (
                    <StyledSvg viewBox={`0 0 ${avatarSize} ${avatarSize}`} width={avatarSize} height={avatarSize}>
                        <StyledCircle {...circleStyles} cx="50%" cy="50%" r={avatarSize / 2} />
                    </StyledSvg>
                )}
                <LazyImage
                    src={category.smallImagePath || defaultImagePath}
                    altText={category.shortDescription}
                    {...styles.avatarImage}
                />
            </StyledWrapper>

            {titlePosition === "below" && <Typography {...styles.avatarText}>{category.shortDescription}</Typography>}
        </Clickable>
    );
};

export default CategoryAvatar;
